/**
 * PKCE (Proof Key for Code Exchange) utilities for the OAuth2 + 2FA login flow.
 *
 * These helpers are used exclusively on the frontend. The code_verifier never
 * leaves the browser — only the code_challenge (its SHA-256 hash) is sent to
 * Moodle. The verifier is stored in localStorage and forwarded to the Python
 * backend during the token-exchange step.
 *
 * NOTE: localStorage is used instead of sessionStorage because some hosting
 * setups (server redirects, service workers) can clear sessionStorage during
 * the OAuth redirect. The values are removed immediately after use.
 */

/**
 * Generates a cryptographically random URL-safe base64 string.
 * @param {number} byteLength - Number of random bytes to generate.
 * @returns {string}
 */
function generateRandomBase64(byteLength) {
  const array = new Uint8Array(byteLength);
  window.crypto.getRandomValues(array);
  return btoa(String.fromCharCode(...array))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/**
 * Generates a PKCE code_verifier (~128 URL-safe chars from 96 random bytes).
 * Must be stored in sessionStorage and sent to the backend during token exchange.
 * @returns {string}
 */
export function generateCodeVerifier() {
  return generateRandomBase64(96);
}

/**
 * Derives the code_challenge from a code_verifier using SHA-256.
 * Only the challenge is sent to Moodle in the authorization URL.
 * @param {string} codeVerifier
 * @returns {Promise<string>} URL-safe base64-encoded SHA-256 hash
 */
export async function generateCodeChallenge(codeVerifier) {
  const data = new TextEncoder().encode(codeVerifier);
  const digest = await window.crypto.subtle.digest('SHA-256', data);
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/**
 * Generates a random state string for CSRF protection.
 * Stored in sessionStorage and verified when Moodle redirects back.
 * @returns {string}
 */
export function generateState() {
  return generateRandomBase64(32);
}

/** Keys used to persist PKCE values in localStorage across the OAuth redirect. */
export const PKCE_KEYS = {
  CODE_VERIFIER : 'oauth_code_verifier',
  STATE         : 'oauth_state',
};
